const getConnection = () => {
    return (
        navigator.connection ||
        navigator.mozConnection ||
        navigator.webkitConnection ||
        null
    );
};

const isSlowConnection = (minDownlinkMbps, maxRttMs) => {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
        return true;
    }

    const connection = getConnection();
    if (!connection) return false;

    const effectiveType = connection.effectiveType || "";
    if (["slow-2g", "2g"].includes(effectiveType)) return true;
    if (connection.saveData === true) return true;

    if (
        typeof connection.downlink === "number" &&
        connection.downlink > 0 &&
        connection.downlink < minDownlinkMbps
    ) {
        return true;
    }

    if (typeof connection.rtt === "number" && connection.rtt > maxRttMs) {
        return true;
    }

    return false;
};

const getBaseUrl = () => {
    const envUrl =
        typeof process !== "undefined" ? process.env.VUE_APP_API_URL : "";
    if (envUrl && envUrl.trim()) {
        return envUrl.replace(/\/+$/, "");
    }
    if (typeof window !== "undefined" && window.location?.origin) {
        return window.location.origin;
    }
    return "";
};

const getPingUrl = () => {
    const baseUrl = getBaseUrl();
    if (baseUrl) return `${baseUrl}/public/ping`;
    return "/public/ping";
};

const measurePing = async (pingUrl, timeoutMs) => {
    if (typeof fetch === "undefined") {
        return { ok: false, status: "no-fetch" };
    }
    const start = Date.now();
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

    try {
        const res = await fetch(`${pingUrl}?_=${start}`, {
            method: "GET",
            cache: "no-store",
            credentials: "include",
            signal: controller.signal,
        });
        window.clearTimeout(timeoutId);
        if (!res.ok) {
            return { ok: false, status: res.status };
        }
        return { ok: true, status: res.status, latencyMs: Date.now() - start };
    } catch (err) {
        window.clearTimeout(timeoutId);
        return { ok: false, status: "error" };
    }
};

export const startNetworkSpeedMonitor = (onChange, options = {}) => {
    if (typeof window === "undefined" || typeof navigator === "undefined") {
        return () => {};
    }
    if (typeof onChange !== "function") {
        return () => {};
    }

    const {
        minDownlinkMbps = 1.5,
        maxRttMs = 800,
        pollIntervalMs = 30000,
        pingUrl = getPingUrl(),
        maxPingMs = 1800,
        pingTimeoutMs = 3000,
        minConsecutiveSlow = 2,
    } = options;

    let current = false;
    let checking = false;
    let consecutiveSlow = 0;
    let pingDisabled = false;

    const evaluateSlow = async () => {
        if (typeof navigator !== "undefined" && navigator.onLine === false) {
            return true;
        }

        const connection = getConnection();
        if (connection) {
            return isSlowConnection(minDownlinkMbps, maxRttMs);
        }

        if (!pingUrl || pingDisabled) return false;

        const result = await measurePing(pingUrl, pingTimeoutMs);
        if (!result || result.status === 404 || result.status === "no-fetch") {
            pingDisabled = true;
            consecutiveSlow = 0;
            return false;
        }

        if (!result.ok || result.latencyMs > maxPingMs) {
            consecutiveSlow += 1;
            return consecutiveSlow >= minConsecutiveSlow;
        }

        consecutiveSlow = 0;
        return false;
    };

    const notify = async () => {
        if (checking) return;
        checking = true;
        const next = await evaluateSlow();
        if (next !== current) {
            current = next;
            onChange(current);
        }
        checking = false;
    };

    const connection = getConnection();
    const handleChange = () => {
        notify();
    };

    window.addEventListener("online", handleChange);
    window.addEventListener("offline", handleChange);
    if (connection && connection.addEventListener) {
        connection.addEventListener("change", handleChange);
    }

    const intervalId = window.setInterval(notify, pollIntervalMs);
    notify();

    return () => {
        window.removeEventListener("online", handleChange);
        window.removeEventListener("offline", handleChange);
        if (connection && connection.removeEventListener) {
            connection.removeEventListener("change", handleChange);
        }
        window.clearInterval(intervalId);
    };
};
